import { useState, useEffect } from 'react';
import { ChevronRight, Search, ChevronsLeft, ChevronsRight, ChevronLeft, MoreVertical, Pencil, Trash2, AlertTriangle, Folder } from 'lucide-react';
import { useGetRoleList, useUpdateRoleStatus, useUpdateRole, useAddRole, useDeleteRole, useGetSpinnerRoleList } from "../../../hooks/useRoles"
import { isError, useQueryClient } from '@tanstack/react-query';
import moment from "moment"
import toast from 'react-hot-toast';

const Roles = () => {
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [rolesList, setRolesList] = useState([]);
  const [spinnerRolesList, setSpinnerRolesList] = useState([]);
  const [deletingRole, setDeletingRole] = useState(null);
  const [showDeleteRoleConfirmation, setShowDeleteRoleConfirmation] = useState(false);
  const [showDropdown, setShowDropdown] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { data: rolesListResponse, isSuccess: isRolesListSuccess } = useGetRoleList(`?page=${currentPage}&limit=${rowsPerPage}&search_key=${searchTerm}`)
  const { data: spinnerRolesListResponse, isSuccess: isSpinnerRolesListSuccess } = useGetSpinnerRoleList()
  const updateMutation = useUpdateRole();
  const addMutation = useAddRole();
  const deleteMutation = useDeleteRole();
  const queryClient = useQueryClient();
  
  const formatText = (str) => {
    return str
      .replace(/_/g, ' ')                      // replace underscores with spaces
      .replace(/^\s*\w/, c => c.toUpperCase()); // capitalize first letter
  }

  const [formData, setFormData] = useState({
    _id: null,
    role: '',
    role_type: '',
    permissions: {
      dashboard: { is_read: 0, is_write: 0 },
      roles: { is_read: 0, is_write: 0 },
      system_users: { is_read: 0, is_write: 0 },
      customers: { is_read: 0, is_write: 0 },
      settings: { is_read: 0, is_write: 0 },
      posts: { is_read: 0, is_write: 0 },
      stories: { is_read: 0, is_write: 0 },
      reels: { is_read: 0, is_write: 0 },
      livestreams: { is_read: 0, is_write: 0 },
      subscriptions: { is_read: 0, is_write: 0 }
    }
  });

  const toggleDropdown = (id) => {
    setShowDropdown(showDropdown === id ? null : id);
  };

  const getPermissionLength = (role) => {
    return Object.values(role?.permissions).filter(
      perm => perm.is_read === 1 || perm.is_write === 1
    ).length;
  }

  // Role types
  const roleTypes = [
    { name: 'Admin', value: 'admin' },
    { name: 'Employee', value: 'employee' },
    { name: 'Manager', value: 'manager' },
    { name: 'Viewer', value: 'viewer' },
  ];

  // Permission sections
  const permissionSections = {
    dashboard: { is_read: 0, is_write: 0 },
    roles: { is_read: 0, is_write: 0 },
    system_users: { is_read: 0, is_write: 0 },
    customers: { is_read: 0, is_write: 0 },
    settings: { is_read: 1, is_write: 0 },
    posts: { is_read: 0, is_write: 0 },
    stories: { is_read: 0, is_write: 0 },
    reels: { is_read: 0, is_write: 0 },
    livestreams: { is_read: 0, is_write: 0 },
    subscriptions: { is_read: 0, is_write: 0 }
  };

  // Handle add button click
  const handleAddClick = () => {
    setFormData({
      role: '',
      role_type: '',
      permissions: {
        dashboard: { is_read: 0, is_write: 0 },
        roles: { is_read: 0, is_write: 0 },
        system_users: { is_read: 0, is_write: 0 },
        customers: { is_read: 0, is_write: 0 },
        settings: { is_read: 0, is_write: 0 },
        posts: { is_read: 0, is_write: 0 },
        stories: { is_read: 0, is_write: 0 },
        reels: { is_read: 0, is_write: 0 },
        livestreams: { is_read: 0, is_write: 0 },
        subscriptions: { is_read: 0, is_write: 0 }
      }
    });
    setModalMode('add');
    setShowModal(true);
  };

  // Handle edit button click
  const handleEditClick = (role) => {
    setFormData({
      _id: role?._id,
      role: role?.role,
      role_type: role?.role_type,
      permissions: role?.permissions
    });
    setModalMode('edit');
    setShowModal(true);
    setShowDropdown(null);
  };

  // Handle delete
  const handleDelete = (role) => {
    setDeletingRole(role)
    setShowDeleteRoleConfirmation(true)
    setShowDropdown(null);
  };

  const handleDeleteRole = () => {
    deleteMutation.mutate(deletingRole?._id, {
      onSuccess: () => {
        toast.success("Role deleted successfully");
        setShowDeleteRoleConfirmation(false)
        queryClient.invalidateQueries({ queryKey: ['roles'] });
      },
      onError: (error) => {
        const message = error.response?.data?.message
        toast.error(message)
      }
    })
  }

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handlePermissionChange = (section, type, checked) => {
    setFormData({
      ...formData,
      permissions: {
        ...formData.permissions,
        [section]: {
          ...formData.permissions[section],
          [type]: checked ? 1 : 0
        }
      }
    });
  };

  const handleSubmit = () => {
    if (!formData?.role || !formData?.role_type) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (modalMode === 'add') {
      addMutation.mutate(formData, {
        onSuccess: () => {
          toast.success('Roles added successfully');
          queryClient.invalidateQueries({ queryKey: ['roles'] });
          setShowModal(false);
        },
        onError: (error) => {
          toast.error(error?.response?.data?.message || 'Failed to add new role')
        }
      })
    } else {
      updateMutation.mutate(formData, {
        onSuccess: () => {
          toast.success('Roles updated successfully');
          queryClient.invalidateQueries({ queryKey: ['roles'] });
          setShowModal(false);
          setModalMode('add')
        },
        onError: (error) => {
          toast.error(error?.response?.data?.message || 'Failed to update role')
        }
      })
    }
  };

  useEffect(() => {
    if (isRolesListSuccess) {
      setRolesList(rolesListResponse?.data?.data?.result)
      let tp = Math.ceil((rolesListResponse?.data?.data?.totalCount) / rowsPerPage)
      setTotalPages(tp == 0 ? 1 : tp)
    }
  }, [rolesListResponse, isRolesListSuccess])

  useEffect(() => {
    if (isSpinnerRolesListSuccess) {
      console.log(spinnerRolesListResponse?.data?.data?.result)
      setSpinnerRolesList(spinnerRolesListResponse?.data?.data?.result)

    }
  }, [spinnerRolesListResponse, isSpinnerRolesListSuccess])

  return (
    <div className="flex min-h-screen bg-gray-50 mb-14">
      <div className="flex-1 flex flex-col">
        <div className="bg-white border-b border-gray-200 px-8 py-4">
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <span>Dashboard</span>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-900">Roles</span>
          </div>
        </div>
        <div className="flex-1 px-1 py-4">
          <div className="max-w-7xl">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Roles ({rolesListResponse?.data?.data?.totalCount})</h1>
                <p className="text-gray-600">Add users and modify permissions to access the admin</p>
              </div>
              <div className="mb-6 mt-4">
                <button
                  onClick={() => handleAddClick()}
                  className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700 flex items-center gap-2">
                  Create New Role
                </button>
              </div>
            </div>
            <div className="mb-6">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by role name"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Permissions
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {rolesList?.map((role) => {
                    const permissions = getPermissionLength(role)
                    return (
                      <tr key={role?._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-900">{role?.role}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">{role?.role_type ? `${role?.role_type[0]?.toUpperCase()}${role?.role_type?.substr(1, role?.role_type?.length)?.toLowerCase()}` : "N/A"}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">{permissions}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="relative inline-block">
                            <button
                              onClick={() => toggleDropdown(role?._id)}
                              className="p-1 hover:bg-gray-100 rounded-full">
                              <MoreVertical size={16} />
                            </button>
                            {showDropdown === role?._id && (
                              <div className="absolute right-0 mt-2 w-48 bg-white rounded shadow-lg border border-gray-200 z-999">
                                <div className="py-1">
                                  <button
                                    onClick={() => handleEditClick(role)}
                                    className="flex px-4 py-2 text-sm w-full text-left hover:bg-gray-100">
                                    <Pencil className='w-4 h-4 mr-1 mt-1' /> Edit
                                  </button>
                                </div>
                                <div className="py-1">
                                  <button
                                    onClick={() => handleDelete(role)}
                                    className="flex px-4 py-2 text-sm w-full text-left hover:bg-gray-100">
                                    <Trash2 className='w-4 h-4 mr-1' />Delete
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              {/* Pagination */}
              <div className="flex justify-end m-4">
                <div className="flex items-center">
                  <div className="mr-4 flex justify-between">
                    <span className="mt-1 mr-2 text-sm text-gray-500">Rows per page</span>
                    <select
                      className="w-[55px] border border-gray-300 rounded p-1 text-sm"
                      value={rowsPerPage}
                      onChange={(e) => { setCurrentPage(1); setRowsPerPage(Number(e.target.value)) }}>
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>
                  </div>
                  <div className="flex items-center text-sm">
                    <span className="mr-4">Page {currentPage} of {totalPages}</span>
                    <div className="flex">
                      <button
                        className="p-1 border border-gray-300 rounded-l hover:bg-gray-100"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}>
                        <ChevronsLeft size={16} />
                      </button>
                      <button
                        className="p-1 border-t border-b border-gray-300 hover:bg-gray-100"
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}>
                        <ChevronLeft size={16} />
                      </button>
                      <button
                        className="p-1 border-t border-b border-gray-300 hover:bg-gray-100"
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}>
                        <ChevronRight size={16} />
                      </button>
                      <button
                        className="p-1 border border-gray-300 rounded-r hover:bg-gray-100"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}>
                        <ChevronsRight size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {showModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-lg w-full max-w-lg">
                <div className="p-6">
                  <h2 className="text-xl font-bold mb-1">{modalMode === 'add' ? 'Add Role' : 'Edit Role'}</h2>
                  <p className="text-gray-500 mb-4">{modalMode === 'add' ? 'Add role' : 'Edit role'}</p>
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">
                      Role name<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="role"
                      placeholder="Role name"
                      className="w-full p-2 border border-gray-300 rounded"
                      value={formData?.role}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Role Type</label>
                    <select
                      name="role_type"
                      className="w-full p-2 border border-gray-300 rounded appearance-none"
                      value={formData?.role_type}
                      onChange={handleInputChange}>
                      <option value="">Select</option>
                      {roleTypes?.map(type => (
                        <option key={type?.value} value={type?.value}>{type?.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">Role Permissions</label>
                    <div className="max-h-60 overflow-y-auto">
                      {Object.keys(permissionSections)?.map((section) => (
                        <div key={section} className="flex items-center py-2 border-b border-gray-100">
                          <div className="w-1/3">{formatText(section)}</div>
                          <div className="w-2/3 flex items-center">
                            <label className="inline-flex items-center mr-4">
                              <input
                                type="checkbox"
                                className="form-checkbox h-4 w-4"
                                checked={formData.permissions[section]?.is_read == 1}
                                onChange={(e) => handlePermissionChange(section, 'is_read', e.target.checked)}
                              />
                              <span className="ml-2">Read</span>
                            </label>
                            <label className="inline-flex items-center">
                              <input
                                type="checkbox"
                                className="form-checkbox h-4 w-4"
                                checked={formData.permissions[section]?.is_write}
                                onChange={(e) => handlePermissionChange(section, 'is_write', e.target.checked)}
                              />
                              <span className="ml-2">Write</span>
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-end mt-6">
                    <button
                      className="px-4 py-2 border border-gray-300 rounded mr-2 hover:bg-gray-50"
                      onClick={() => setShowModal(false)}>
                      Cancel
                    </button>
                    <button
                      className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700"
                      onClick={handleSubmit}>
                      Done
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* Delete Roles Confirmation Modal */}
          {showDeleteRoleConfirmation && deletingRole && (
            <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
              <div className="bg-white rounded-md shadow-lg w-full max-w-md">
                <div className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-500">
                      <AlertTriangle size={20} />
                    </div>
                    <h2 className="text-xl font-semibold">Delete Role</h2>
                  </div>
                  <p className="text-gray-600 mb-4">
                    Are you sure you want to delete this role? This action cannot be undone.
                  </p>
                  <div className="p-4 border rounded bg-gray-50 mb-4">
                    <div className="flex items-center mb-2">
                      <Folder size={16} className="mr-2 text-gray-500" />
                      <p className="font-medium">{deletingRole.role}</p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end p-4 bg-gray-50 border-t border-gray-200 rounded-b-md">
                  <button
                    onClick={() => {
                      setDeletingRole(null);
                      setShowDeleteRoleConfirmation(false);
                    }}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded mr-2">
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteRole}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default Roles;